import { t } from 'i18next';

import { useEffect, useState } from 'react';
import Modal from '../../../../components/Modal';
import SelectAsyncJSX from '../../../../components/SelectAsync';
import SingleSelect from '../../../../components/SingleSelect';
import {
  billingUnitApi,
  branchApi,
  customersApi,
  orderTypesApi,
  sellersApi,
  tenantManagerApi,
  tenantSupervisorApi,
} from '../../../../services/requests';
import { Customer, Seller } from '../../../../types';
import { BillingUnit } from '../../../../types/apiResponse/BillingUnit';
import { Branch } from '../../../../types/apiResponse/Branch';
import { OrdersFiltersInFrontend, OrderType } from '../../../../types/apiResponse/order';
import { TenantManager } from '../../../../types/apiResponse/TenantManager';
import { TenantSupervisor } from '../../../../types/apiResponse/TenantSupervisor';
import {
  exportedErpSelectOptions,
  filterTypeSelectOptions,
  getOptionByValue,
  orderFreightTypeSelectOptions,
  orderOriginSelectOptions,
  orderStatusSelectOptions,
  SelectOption,
} from '../../../../utils/getSelectOptions';
import { defaultOrderFilters } from '../constants/defaultColumnsConfig';
import useOrderFilters from '../hooks/useOrderFilters';
import OrderFilterDateInput from './OrderFilterDateInput';

type Props = {
  closeModal: () => void;
  setStoredOrderFilters: (orderFilters: OrdersFiltersInFrontend) => void;
  isLoading: boolean;
};

const OrderFiltersModal = ({ closeModal, setStoredOrderFilters, isLoading }: Props) => {
  const [storedOrderFilters] = useOrderFilters();

  const [orderFilters, setOrderFilters] = useState<OrdersFiltersInFrontend>(storedOrderFilters);

  const [tenantSupervisorQuery, setTenantSupervisorQuery] = useState<string | undefined>();
  const [sellerQuery, setSellerQuery] = useState<string | undefined>();
  const [customerQuery, setCustomerQuery] = useState<string | undefined>();

  const resetFilters = () => {
    setOrderFilters(defaultOrderFilters);
  };

  useEffect(() => {
    if (orderFilters.tenantManagerCode) {
      setTenantSupervisorQuery(`tenantManagerCode=${orderFilters.tenantManagerCode}`);
    } else {
      setTenantSupervisorQuery(undefined);
    }

    if (orderFilters.tenantSupervisorCode) {
      setSellerQuery(`tenantSupervisorCode=${orderFilters.tenantSupervisorCode}`);
    } else {
      setSellerQuery(undefined);
    }

    if (orderFilters.sellerCode) {
      setCustomerQuery(`sellerCode=${orderFilters.sellerCode}`);
    } else {
      setCustomerQuery(undefined);
    }
  }, [orderFilters.tenantManagerCode, orderFilters.tenantSupervisorCode, orderFilters.sellerCode]);

  const handleAsyncSelectValue = (
    codeKey: keyof OrdersFiltersInFrontend,
    descriptionKey: keyof OrdersFiltersInFrontend,
  ): SelectOption | null => {
    if (orderFilters[codeKey] && orderFilters[descriptionKey]) {
      return {
        label: `${orderFilters[codeKey]} - ${orderFilters[descriptionKey]}`,
        value: orderFilters[codeKey] as string,
      };
    }
    return null;
  };

  const handleAsyncSelectOnChange = (
    codeKey: keyof OrdersFiltersInFrontend,
    descriptionKey: keyof OrdersFiltersInFrontend,
  ) => {
    // Retorna uma função que lida com o `onChange`
    return (option: SelectOption | null) => {
      if (option) {
        setOrderFilters({
          ...orderFilters,
          [codeKey]: option.value as string,
          [descriptionKey]: option.label.replace(`${option.value} - `, ''),
        });
      } else {
        setOrderFilters({
          ...orderFilters,
          [codeKey]: '',
          [descriptionKey]: '',
        });
      }
    };
  };

  const operationalStatusOptions = [
    { value: '', label: 'Todos' },
    { value: 'processing', label: 'Em processamento' },
    { value: 'awaitingApproval', label: 'Aguardando aprovação' },
    { value: 'rejected', label: 'Rejeitado' },
    { value: 'completed', label: 'Concluído' },
    { value: 'error', label: 'Erro' },
    { value: 'cancelled', label: 'Cancelado' },
  ];

  const budgetOptions = [
    { value: 'all', label: t('both') },
    { value: false, label: t('not') },
    { value: true, label: t('yes') },
  ];

  return (
    <Modal
      title={t('orderFilters')}
      showModal={true}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={() => setStoredOrderFilters(orderFilters)}
      actionNameBtn={t('toFilter')}
      additionalActions={[{ name: t('clearFilters'), onClick: () => resetFilters() }]}
      bodyStyle={orderFilters.filterType === 'detailed' ? { overflow: 'auto' } : {}}
    >
      <>
        <div className="row" style={{ justifyContent: 'flex-start', marginTop: '-0.5rem' }}>
          <div className="inputWithLabel" style={{ marginTop: 0, maxWidth: '200px' }}>
            <p className="label">{t('filterType') as string}</p>
            <SingleSelect
              options={filterTypeSelectOptions()}
              onChange={(option: SelectOption) =>
                setOrderFilters({ ...orderFilters, filterType: option.value as any })
              }
              value={getOptionByValue(orderFilters.filterType, filterTypeSelectOptions())}
              disabled={isLoading}
            />
          </div>

          <div className="inputWithLabel">
            <label className="label" htmlFor="order-filter-number">{t('orderNumber') as string}</label>
            <input
              id="order-filter-number"
              inputMode="numeric"
              placeholder={t('orderNumber')}
              onChange={(e: any) =>
                setOrderFilters({ ...orderFilters, orderNumber: e.target.value })
              }
              value={orderFilters.orderNumber}
              className="input"
              disabled={isLoading}
            />
          </div>
        </div>

        {orderFilters.filterType == 'detailed' && (
          <>
            <div className="row">
              <div className="inputWithLabel">
                <p className="label">Andamento</p>
                <SingleSelect
                  options={operationalStatusOptions}
                  value={getOptionByValue(orderFilters.operationalStatus || '', operationalStatusOptions)}
                  onChange={(option: SelectOption) => setOrderFilters({
                    ...orderFilters,
                    operationalStatus: option.value as OrdersFiltersInFrontend['operationalStatus'],
                  })}
                  style={{ minWidth: '120px' }}
                  disabled={isLoading}
                />
              </div>
              <label className="inputWithLabel">
                <span className="label">Status no ERP</span>
                <input className="input" aria-label="Status no ERP" disabled={isLoading}
                  value={orderFilters.erpStatus || ''} placeholder="Status retornado pelo sistema externo"
                  onChange={(event) => setOrderFilters({ ...orderFilters, erpStatus: event.target.value })} />
              </label>
            </div>
            <div className="row">
              <div className="inputWithLabel">
                <p className="label">{t('orderStatus') as string}</p>
                <SingleSelect
                  options={orderStatusSelectOptions()}
                  onChange={(option: SelectOption) =>
                    setOrderFilters({ ...orderFilters, orderStatus: option.value as any })
                  }
                  value={getOptionByValue(orderFilters.orderStatus, orderStatusSelectOptions())}
                  style={{ minWidth: '120px' }}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('isBudget') as string}</p>
                <SingleSelect
                  options={budgetOptions}
                  onChange={(option: any) =>
                    setOrderFilters({ ...orderFilters, isBudget: option.value as any })
                  }
                  value={getOptionByValue(orderFilters.isBudget ?? 'all', budgetOptions)}
                  style={{ minWidth: '120px' }}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <div className="label">{t('orderType') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue('orderTypeCode', 'orderTypeDescription')}
                  onChange={handleAsyncSelectOnChange('orderTypeCode', 'orderTypeDescription')}
                  request={orderTypesApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: OrderType) =>
                        `${item.CodigoTipoPed} - ${item.DescricaoTipoPed}`,
                      value: (item: OrderType) => item.CodigoTipoPed,
                    },
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('orderOrigin') as string}</p>
                <SingleSelect
                  options={orderOriginSelectOptions()}
                  onChange={(option: SelectOption) =>
                    setOrderFilters({ ...orderFilters, orderOriginCode: option.value as any })
                  }
                  value={getOptionByValue(orderFilters.orderOriginCode, orderOriginSelectOptions())}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="row">
              <div className="inputWithLabel">
                <div className="label">{t('manager') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue('tenantManagerCode', 'tenantManagerDescription')}
                  onChange={handleAsyncSelectOnChange(
                    'tenantManagerCode',
                    'tenantManagerDescription',
                  )}
                  request={tenantManagerApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: TenantManager) => `${item.ger_Codigo} - ${item.ger_Nome}`,
                      value: (item: TenantManager) => item.ger_Codigo,
                    },
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <div className="label">{t('supervisor') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue(
                    'tenantSupervisorCode',
                    'tenantSupervisorDescription',
                  )}
                  onChange={handleAsyncSelectOnChange(
                    'tenantSupervisorCode',
                    'tenantSupervisorDescription',
                  )}
                  request={tenantSupervisorApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: TenantSupervisor) => `${item.sup_Codigo} - ${item.sup_Nome}`,
                      value: (item: TenantSupervisor) => item.sup_Codigo,
                    },
                  }}
                  query={tenantSupervisorQuery}
                  cacheUniqs={[tenantSupervisorQuery]}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <div className="label">{t('seller') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue('sellerCode', 'sellerName')}
                  onChange={handleAsyncSelectOnChange('sellerCode', 'sellerName')}
                  request={sellersApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: Seller) => `${item.CodigoVendedor} - ${item.Nome}`,
                      value: (item: Seller) => item.CodigoVendedor,
                    },
                  }}
                  query={sellerQuery}
                  cacheUniqs={[sellerQuery]}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <div className="label">{t('customer') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue('customerCode', 'customerName')}
                  onChange={handleAsyncSelectOnChange('customerCode', 'customerName')}
                  request={customersApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: Customer) => `${item.CodigoCliente} - ${item.RazaoSocial}`,
                      value: (item: Customer) => item.CodigoCliente,
                    },
                  }}
                  query={customerQuery}
                  cacheUniqs={[customerQuery]}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="row">
              <div className="inputWithLabel">
                <div className="label">{t('branch') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue('orderTypeCode', 'orderTypeDescription')}
                  onChange={handleAsyncSelectOnChange('orderTypeCode', 'orderTypeDescription')}
                  request={branchApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: Branch) => `${item.filial_Codigo} - ${item.filial_Descricao}`,
                      value: (item: Branch) => item.filial_Codigo,
                    },
                  }}
                  disabled
                />
              </div>

              <div className="inputWithLabel">
                <div className="label">{t('billingUnit') as string}</div>
                <SelectAsyncJSX
                  value={handleAsyncSelectValue('billingUnitCode', 'billingUnitDescription')}
                  onChange={handleAsyncSelectOnChange('billingUnitCode', 'billingUnitDescription')}
                  request={billingUnitApi}
                  reqResponseToOption={{
                    mapper: {
                      label: (item: BillingUnit) =>
                        `${item.CodigoUnidFat} - ${item.DescricaoUnidFat}`,
                      value: (item: BillingUnit) => item.CodigoUnidFat,
                    },
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('freightType') as string}</p>
                <SingleSelect
                  options={orderFreightTypeSelectOptions()}
                  onChange={(option: SelectOption) =>
                    setOrderFilters({ ...orderFilters, freightType: option.value as any })
                  }
                  value={getOptionByValue(
                    orderFilters.freightType,
                    orderFreightTypeSelectOptions(),
                  )}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('exportedOrder') as string}</p>
                <SingleSelect
                  options={exportedErpSelectOptions()}
                  onChange={(option: SelectOption) =>
                    setOrderFilters({ ...orderFilters, exportedErp: option.value as any })
                  }
                  value={getOptionByValue(orderFilters.exportedErp, exportedErpSelectOptions())}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="row">
              <div className="inputWithLabel">
                <p className="label">{t('orderCreationDate') as string}</p>
                <p className="label">{t('start') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderCreationDate')} - ${t('start')}`}
                  value={orderFilters.initialOrderCreationDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, initialOrderCreationDate: value })}
                  disabled={isLoading}
                />
                <p className="label">{t('end') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderCreationDate')} - ${t('end')}`}
                  value={orderFilters.finalOrderCreationDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, finalOrderCreationDate: value })}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('orderSubmissionDate') as string}</p>
                <p className="label">{t('start') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderSubmissionDate')} - ${t('start')}`}
                  value={orderFilters.initialOrderSendDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, initialOrderSendDate: value })}
                  disabled={isLoading}
                  withTime
                />
                <p className="label">{t('end') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderSubmissionDate')} - ${t('end')}`}
                  value={orderFilters.finalOrderSendDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, finalOrderSendDate: value })}
                  disabled={isLoading}
                  withTime
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('orderDeliveryDate') as string}</p>
                <p className="label">{t('start') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderDeliveryDate')} - ${t('start')}`}
                  value={orderFilters.initialOrderDeliveryDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, initialOrderDeliveryDate: value })}
                  disabled={isLoading}
                />
                <p className="label">{t('end') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderDeliveryDate')} - ${t('end')}`}
                  value={orderFilters.finalOrderDeliveryDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, finalOrderDeliveryDate: value })}
                  disabled={isLoading}
                />
              </div>

              <div className="inputWithLabel">
                <p className="label">{t('orderBillingDate') as string}</p>
                <p className="label">{t('start') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderBillingDate')} - ${t('start')}`}
                  value={orderFilters.initialOrderBillingDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, initialOrderBillingDate: value })}
                  disabled={isLoading}
                />
                <p className="label">{t('end') as string}</p>
                <OrderFilterDateInput
                  label={`${t('orderBillingDate')} - ${t('end')}`}
                  value={orderFilters.finalOrderBillingDate}
                  onChange={(value) => setOrderFilters({ ...orderFilters, finalOrderBillingDate: value })}
                  disabled={isLoading}
                />
              </div>
            </div>
          </>
        )}
      </>
    </Modal>
  );
};

export default OrderFiltersModal;
